/**
 * Entry point for the AMP Beauty chat widget.
 *
 * Loads as `<script src="…/embed.js" defer></script>`. On execution:
 *   1. Creates a host element on the page (a top-level div).
 *   2. Attaches a closed shadow DOM so our CSS can't leak to the host theme.
 *   3. Injects the widget stylesheet.
 *   4. Mounts the Preact app inside the shadow root.
 *
 * Idempotent: if the script is loaded twice (e.g. theme app embed + a stray
 * script tag), we bail rather than mount two widgets.
 */

import { render, h } from "preact";
import { Widget } from "./Widget.tsx";
import { PdpInline } from "./PdpInline.tsx";
import { STYLES } from "./styles.ts";
import { detectProductHandle } from "./store.ts";
import { getBackend } from "./api.ts";
import { track } from "./analytics.ts";

const HOST_ID = "amp-ai-sales-widget-host";
const PDP_HOST_ID = "amp-ai-sales-pdp-host";
const PDP_MOUNT_TARGET_ID = "amp-pdp-chat-mount";

function init() {
  if (typeof document === "undefined") return;
  if (document.getElementById(HOST_ID)) return; // already mounted

  // Load AMP brand fonts at the document level so shadow DOM CSS can
  // reference them. Fonts loaded by the document are inherited into shadow
  // trees. AMP's DESIGN.md mandates Horizon (display) + Poppins (body),
  // self-hosted from our backend origin. `font-display: swap` avoids
  // invisible text while the font downloads.
  injectBrandFonts();

  const host = document.createElement("div");
  host.id = HOST_ID;
  // Don't let the host page accidentally style us — the shadow does the
  // real isolation, but `all: initial` on :host catches edge cases.
  host.style.all = "initial";
  document.body.appendChild(host);

  // Open shadow so dev tooling and tests can introspect. The host page
  // gains access to host.shadowRoot, but it could already see the live
  // DOM via our entry script anyway — closed gives ~zero security and
  // makes debugging painful.
  const shadow = host.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = STYLES;
  shadow.appendChild(style);

  const mountPoint = document.createElement("div");
  shadow.appendChild(mountPoint);

  render(h(Widget, {}), mountPoint);

  // Conditionally mount the inline PDP block. It rides on the floating
  // widget — clicking a chip in the inline block opens the modal and
  // sends the message via chatStore.
  mountPdpInline();

  // Analytics: one event per page-load with the widget mounted (the floating
  // bubble itself is off; see SHOW_FLOATING_BUBBLE in Widget.tsx). This
  // is our denominator for the engagement funnel ("of N storefront views
  // with a bubble, how many opened a modal?"). Fire after mount so any
  // mount failure shows up as missing events, not as a wrong baseline.
  track("bubble_view", {
    productHandle: detectProductHandle() ?? undefined,
  });
}

/**
 * Mount the inline PDP block if either:
 *   - The host page exposes an explicit mount target: `<div id="amp-pdp-chat-mount"></div>`.
 *     This is the merchant-controlled placement; merchant drops it where they want.
 *   - We're on a /products/<handle> path AND no explicit mount exists. In this case
 *     we inject before the closing `</main>` as a sane default. Merchants who want
 *     control should use the explicit target.
 *
 * Idempotent — safe if main.tsx loads twice.
 */
function mountPdpInline(): void {
  if (document.getElementById(PDP_HOST_ID)) return; // already mounted

  const productHandle = detectProductHandle();
  const explicitTarget = document.getElementById(PDP_MOUNT_TARGET_ID);

  // If we're not on a PDP and there's no explicit target, skip entirely.
  if (!productHandle && !explicitTarget) return;

  const host = document.createElement("div");
  host.id = PDP_HOST_ID;
  host.style.all = "initial";
  host.style.display = "block";

  if (explicitTarget) {
    explicitTarget.appendChild(host);
  } else {
    // Auto-inject heuristic: try to slot it after the product description
    // or before </main>. This is a best-effort default for merchants who
    // haven't placed the explicit mount target.
    const main = document.querySelector("main") || document.body;
    main.appendChild(host);
  }

  const shadow = host.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = STYLES;
  shadow.appendChild(style);

  const mountPoint = document.createElement("div");
  shadow.appendChild(mountPoint);

  render(h(PdpInline, { productHandle }), mountPoint);
}

function injectBrandFonts(): void {
  // Idempotent — bail if we've already added these (e.g., the script loaded twice).
  if (document.head.querySelector('style[data-amp-widget-font]')) return;

  // Self-hosted from the widget's backend origin in production (Railway
  // serves /fonts/* with CORS + immutable cache headers). In local dev
  // the page itself is the origin and Vite serves /public/fonts/, so we
  // skip the backend prefix to avoid hitting prod from a localhost dev
  // session. We also avoid `window.AMP_BACKEND_URL` conflicts: a dev who
  // wants to point chat at prod won't accidentally pull fonts from prod
  // too.
  const isLocalDev =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  const base = isLocalDev ? "" : getBackend();
  const css = `
@font-face {
  font-family: "Horizon";
  src: url("${base}/fonts/Horizon.otf") format("opentype");
  font-weight: 400 700;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Poppins";
  src: url("${base}/fonts/Poppins-Regular.otf") format("opentype");
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Poppins";
  src: url("${base}/fonts/Poppins-Medium.otf") format("opentype");
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Poppins";
  src: url("${base}/fonts/Poppins-SemiBold.otf") format("opentype");
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Poppins";
  src: url("${base}/fonts/Poppins-Bold.otf") format("opentype");
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}
`;

  const styleEl = document.createElement("style");
  styleEl.setAttribute("data-amp-widget-font", "");
  styleEl.textContent = css;
  document.head.appendChild(styleEl);
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}
